// Layer 3 (Transformations): the ranked six-issue list shared by the
// Metallurgist "Active AI cases" tab and the Senior Metallurgist
// "Diagnostic cases" tab — Paul's `renderIssueList(roleKey)`
// (map-role-dashboards.md §2 C5). Rule ids are stripped on render; MTD
// impact is computed from the seed clock; cursor-relative dates are applied
// here so the spine stays month-agnostic.

import {
  ISSUE_SPINE,
  SMBS_DOSE_PCT,
  SMBS_DOSE_PROPOSAL,
  issueSpineTone,
  tarpTierTone,
  type IssueSpineRow,
} from '@/versions/v1/seed/bunker-hill-issue-spine';
import type { PillProps } from '@/design-system/Pill';
import { DRIVER_TREE_SEED } from '@/versions/v1/shared/bunker-hill-driver-tree-seed';
import type { PricedDriverTree } from '@/versions/v1/schema/driver-tree';
import {
  formatExposurePerDay,
  issueExposureView,
  pricedDriverTree,
  renderedImpactNote,
  subCaption,
} from '@/versions/v1/modules/advisory/DriverTree/Scene.transform';
import type { VizTone } from '@/versions/v1/modules/_viz';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { smelterPenaltyLimit } from '@/versions/v1/shared/bunker-hill-smelter-terms';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { seriesAt } from '@/versions/v1/shared/series.derive';
import { formatDateOffsetFromCursor, shiftValueAt } from './LandingCursor.transform';
import { feedDesignStph, feedStphAt, formatMtdImpact, formatPptGap, mtdDaysElapsed, p80DesignMicrons, znGapPpt } from './LandingSeries.transform';
import { FAILURE_MODES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { bm01BearingTrend, bm01BearingTrendPhrase } from '@/versions/v1/modules/downtime/ValueLost.transform';
import { CURRENCY_GLYPH } from '@/versions/v1/shared/money';

export const ISSUE_LIST_FIRST_PAINT_COUNT = 5;

export type IssueListRoleKey = 'cmet' | 'smet' | 'supt' | 'maint' | 'mgr';

export type IssueListRow = {
  id: string;
  rank: number;
  title: string;
  area: string;
  tier: IssueSpineRow['tier'];
  tierTone: NonNullable<PillProps['tone']>;
  impactUnitLabel: `${typeof CURRENCY_GLYPH}/day` | `${typeof CURRENCY_GLYPH}/one-off`;
  impactLabel: string;
  impactMuted: boolean;
  impactBarTone: VizTone;
  attributionLabel?: string;
  contributorLabel?: string;
  secondaryImpactLabel?: string;
  ownerBreakdown: { issueId: string; label: string; amountLabel: string; href: string }[];
  impType: string;
  impactBarPct: number;
  tone: 'bad' | 'warn' | 'info';
  body: string;
  caseId?: string;
  driverTreeHref: string;
  driverTreePathHint: string;
};

/** Paul's Met-level issues: his Control metallurgist tab names `Issues 2, 3, 4
 *  — P80 and Zn recovery and CuSO4`, so this set is the issue *level*, not the
 *  cases the Metallurgist owns — Advisory assigns GRIND-P80 to maintenance and
 *  CUSO4-ACT to operator (`casesWithSession(...).assignedTo`). The one source
 *  for the landing's `Met-level issues` strip cell and rail (polish.P26 r1). */
export const MET_LEVEL_ISSUE_IDS: readonly string[] = ['iss-p80-excursion', 'iss-zn-recovery', 'iss-cuso4-batch'];

const ISSUE_CASE_IDS: Record<string, string> = {
  'iss-bm01-bearing': 'case-bm-brg',
  'iss-p80-excursion': 'case-p80',
  'iss-zn-recovery': 'case-zn-recovery',
  'iss-cuso4-batch': 'case-cuso4',
  'iss-lime-silo': 'case-lime-trip',
  'iss-pb-in-zn-con': 'case-smbs-fmea',
};

/** The Advisory case an issue row opens, if it has one. */
export function issueCaseId(issueId: string): string | undefined {
  return ISSUE_CASE_IDS[issueId];
}

function driverIssueFor(row: IssueSpineRow, seed: PricedDriverTree) {
  const issue = seed.issues.find((candidate) => candidate.rank === row.rank);
  if (!issue) throw new Error(`issue list: no driver-tree exposure row for rank ${row.rank}`);
  return issue;
}

function issueAmountPerDay(row: IssueSpineRow, seed: PricedDriverTree): number {
  return issueExposureView(driverIssueFor(row, seed), seed).amountPerDay;
}

const RULE_ID_PATTERN =
  /\b(?:MD|CY|SOP|TARP|SA)-[A-Z0-9-]+\b|\bTier 1 active\b/gi;

function stripRuleIds(text: string): string {
  return text
    .replace(RULE_ID_PATTERN, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\(\s*\)/g, '')
    .replace(/\s+([.,;:])/g, '$1')
    .trim();
}

function applySeriesSubstitutions(text: string, state: SessionState): string {
  const p80 = seriesAt('rs-sa01-p80', state).latest?.value ?? readingByIdLookup('rd-spec-p80')?.value ?? 82;
  const p80Design = p80DesignMicrons(state);
  const ph = seriesAt('rs-zn-ph', state).latest?.value ?? 10.2;
  const cuso4Dose = seriesAt('rs-reag-cuso4', state).latest?.value ?? 155;
  const batch = readingByIdLookup('rd-cuso4-batch-strength');
  const batchValue = batch?.value ?? 162;
  const batchDesign = batch?.design ?? 180;
  const feed = feedStphAt(state);
  const feedDesign = feedDesignStph(state);
  const feedBelowPct = feedDesign > 0 ? ((feedDesign - feed) / feedDesign) * 100 : 0;
  return text
    .replace(/\{\{P80\}\}/g, String(Math.round(p80)))
    .replace(/\{\{P80_DESIGN\}\}/g, String(p80Design))
    .replace(/\{\{PH\}\}/g, ph.toFixed(1))
    .replace(/\{\{CUSO4_DOSE\}\}/g, String(Math.round(cuso4Dose)))
    .replace(/\{\{CUSO4_BATCH\}\}/g, String(batchValue))
    .replace(/\{\{CUSO4_BATCH_DESIGN\}\}/g, String(batchDesign))
    .replace(/\{\{FEED\}\}/g, feed.toFixed(1))
    .replace(/\{\{FEED_DESIGN\}\}/g, feedDesign.toFixed(1))
    .replace(/\{\{FEED_BELOW_PCT\}\}/g, feedBelowPct.toFixed(1));
}

function pbInZnPct(state: SessionState): string {
  return (shiftValueAt('rs-spec-zn-pb', state.cursor, state) ?? 2.6).toFixed(1);
}

/** Headroom to the Zn smelter's Pb penalty limit, in points, from the same
 *  Pb in Zn figure the title prints (3.0 − 2.6 = 0.4 at the demo cursor). */
export function pbInZnHeadroomPt(state: SessionState): string {
  return (smelterPenaltyLimit('Zn', 'Pb') - Number(pbInZnPct(state))).toFixed(1);
}

/** Replace spine placeholders and legacy month literals with cursor-relative copy. */
function applyCursorRelativeCopy(text: string, state: SessionState): string {
  const recoveryDay = formatDateOffsetFromCursor(state, 7);
  const blendDay = formatDateOffsetFromCursor(state, 7);
  const baselineDay = formatDateOffsetFromCursor(state, -4);
  return text
    .replace(/\{\{PB_IN_ZN\}\}/g, pbInZnPct(state))
    .replace(/\{\{RECOVERY_DAY\}\}/g, recoveryDay)
    .replace(/\{\{BLEND_DAY\}\}/g, blendDay)
    .replace(/\{\{BASELINE_DAY\}\}/g, baselineDay)
    .replace(/\b15 Sep\b/g, recoveryDay)
    .replace(/\b19 Sep\b/g, formatDateOffsetFromCursor(state, 11))
    .replace(/\b16 Sep\b/g, formatDateOffsetFromCursor(state, 8))
    .replace(/\b4 Sep\b/g, baselineDay);
}

function issueTitle(row: IssueSpineRow, state: SessionState): string {
  if (row.id === 'iss-pb-in-zn-con') {
    return `Pb in Zn con ${pbInZnPct(state)}% — trending toward ${smelterPenaltyLimit('Zn', 'Pb').toFixed(1)}% smelter penalty`;
  }
  return applyCursorRelativeCopy(row.title, state);
}

function issueBody(row: IssueSpineRow, roleKey: IssueListRoleKey, state: SessionState, seed: PricedDriverTree): string {
  let body = stripRuleIds(row.text[roleKey]);
  body = applySeriesSubstitutions(body, state);
  body = applyCursorRelativeCopy(body, state);

  if (row.id === 'iss-pb-in-zn-con') {
    body = body
      .replace(/2\.4%/g, `${pbInZnPct(state)}%`)
      .replace(/\{\{PB_IN_ZN_HEADROOM\}\}/g, pbInZnHeadroomPt(state))
      .replace(/\{\{SMBS_DOSE_PCT\}\}/g, String(SMBS_DOSE_PCT))
      .replace(/\{\{SMBS_DOSE_FROM\}\}/g, String(SMBS_DOSE_PROPOSAL.fromGpt))
      .replace(/\{\{SMBS_DOSE_TO\}\}/g, String(SMBS_DOSE_PROPOSAL.toGpt));
  }

  if (row.id === 'iss-bm01-bearing') {
    const bearingTrend = bm01BearingTrend(state);
    const bearingReading = readingByIdLookup('rd-bm-brg-temp');
    const fmea = FAILURE_MODES.find((item) => item.id === 'fm-trunnion');
    if (!fmea) throw new Error('issue list: missing fm-trunnion FMEA row');
    if (bearingReading?.band?.hi === undefined || bearingReading.spec?.hi === undefined) {
      throw new Error('issue list: missing BM01 warning or specification limit');
    }
    body = body
      .replace(/\{\{BEARING_CURRENT\}\}/g, bearingTrend.currentValue === null ? 'unmeasured' : bearingTrend.currentValue.toFixed(1))
      .replace(/\{\{BEARING_WARNING\}\}/g, String(bearingReading.band.hi))
      .replace(/\{\{BEARING_LIMIT\}\}/g, String(bearingReading.spec.hi))
      .replace(/\{\{BEARING_TREND\}\}/g, bm01BearingTrendPhrase(bearingTrend))
      .replace(/RPN\s+\d+/g, `RPN ${fmea.rpn}`);
  }

  if (row.id === 'iss-zn-recovery' && roleKey === 'mgr') {
    const gap = formatPptGap(state);
    const amount = issueAmountPerDay(row, seed);
    const mtd = formatMtdImpact(amount, state);
    const days = mtdDaysElapsed(state.cursor);
    body = `${gap} below design at ${formatExposurePerDay(amount)}/day. MTD impact at ${days} days: ${mtd}. Full recovery not expected until ${formatDateOffsetFromCursor(state, 7)} Day shift.`;
  }
  if (row.id === 'iss-zn-recovery' && roleKey === 'smet') {
    // Recovery grammar: the Senior metallurgist's rows read the gap in `pt`.
    const gap = `${znGapPpt(state).toFixed(1)} pt`;
    body = `Attribution: pH, CuSO4 dose and P80 acting simultaneously — consistent with observed ${gap} gap. Sequence: CuSO4 batch first (~10:00), then pH recovery (2−4h lag), then P80 decision at 14:00.`;
  }
  if (row.id === 'iss-zn-recovery' && roleKey === 'supt') {
    body = `${formatExposurePerDay(issueAmountPerDay(row, seed))}/day active loss. Okoye managing. Cannot close until ${formatDateOffsetFromCursor(state, 7)} assays show Zn above design floor.`;
  }
  if (row.id === 'iss-cuso4-batch' && roleKey === 'supt') {
    body = `Approximately 0.8 pt of the ${formatPptGap(state)} gap. Control Met (Okoye) owns this action.`;
  }
  return body;
}

function tierPillTone(tier: IssueSpineRow['tier']): NonNullable<PillProps['tone']> {
  const tone = tarpTierTone(tier);
  if (tone === 'bad') return 'danger';
  if (tone === 'warn') return 'warning';
  return 'default';
}

export function issueListVisibleRows(
  rows: readonly IssueListRow[],
  showAll: boolean,
): IssueListRow[] {
  return showAll ? [...rows] : rows.slice(0, ISSUE_LIST_FIRST_PAINT_COUNT);
}

export function issueListHiddenCount(rows: readonly IssueListRow[]): number {
  return Math.max(0, rows.length - ISSUE_LIST_FIRST_PAINT_COUNT);
}

export function issueListRows(
  roleKey: IssueListRoleKey,
  projectSlug: string,
  state: SessionState,
): IssueListRow[] {
  const pricedSeed = pricedDriverTree(DRIVER_TREE_SEED, state);
  const impactBarBasis = Math.max(...pricedSeed.issues.map((issue) => issue.impactPerDay));
  return ISSUE_SPINE.map((row) => {
    const tone = issueSpineTone(row);
    const driverIssue = driverIssueFor(row, pricedSeed);
    const exposure = issueExposureView(driverIssue, pricedSeed, state.cursor);
    const attribution = subCaption(renderedImpactNote(driverIssue, pricedSeed, 'metallurgist'));
    const owner = exposure.ownerRank ? `#${exposure.ownerRank}` : undefined;
    return {
      id: row.id,
      rank: row.rank,
      title: issueTitle(row, state),
      area: row.area,
      tier: row.tier,
      tierTone: tierPillTone(row.tier),
      impactUnitLabel: exposure.stopComparison ? `${CURRENCY_GLYPH}/one-off` : `${CURRENCY_GLYPH}/day`,
      impactLabel: exposure.stopComparison
        ? `${formatExposurePerDay(exposure.stopComparison.plannedAmount)} vs ${formatExposurePerDay(exposure.stopComparison.avoidedFailureAmount)}`
        : `${formatExposurePerDay(exposure.amountPerDay)}/day`,
      impactMuted: exposure.contributor,
      impactBarTone: exposure.contributor ? 'info' : tone,
      ...(exposure.contributor && owner
        ? { contributorLabel: `${attribution} · within ${owner}` }
        : { attributionLabel: attribution }),
      ...(exposure.stopComparison
        ? { secondaryImpactLabel: `${exposure.stopComparison.plannedHours} h planned inspection · ${exposure.stopComparison.avoidedFailureHours} h avoided failure` }
        : exposure.recoverySharePerDay !== undefined && owner
        ? { secondaryImpactLabel: `Recovery share ${formatExposurePerDay(exposure.recoverySharePerDay)}/day · within ${owner}` }
        : {}),
      ownerBreakdown: exposure.breakdown.map((share) => ({
        issueId: share.issueId,
        label: share.label,
        amountLabel: `${formatExposurePerDay(share.amountPerDay)}/day`,
        href: `/projects/${projectSlug}/advisory/driver-tree#driver-issue-${share.issueId}`,
      })),
      impType: row.impType,
      impactBarPct: exposure.stopComparison ? 0 : Math.min(100, Math.round((exposure.barAmountPerDay / impactBarBasis) * 100)),
      tone,
      body: issueBody(row, roleKey, state, pricedSeed),
      caseId: ISSUE_CASE_IDS[row.id],
      driverTreeHref: `/projects/${projectSlug}/advisory/driver-tree`,
      driverTreePathHint: row.driverTreePath,
    };
  });
}

/** How a row's figure column draws (polish.P27 r1): the amount split on its
 *  ` vs ` so each side holds whole, the tier pill's state tone, whether the
 *  row is a one-off (no magnitude bar) and the note under the figure. */
export type IssueRowFigureView = {
  parts: string[];
  tierState: 'bad' | 'warn' | 'neutral';
  oneOff: boolean;
  /** The note under the figure; each ` · ` holds to the part before it, so a
   *  wrapped line never opens on a separator. */
  note: string;
};

const TIER_STATE: Record<IssueListRow['tierTone'], IssueRowFigureView['tierState']> = {
  danger: 'bad',
  warning: 'warn',
  default: 'neutral',
  success: 'neutral',
  admin: 'neutral',
};

export function issueRowFigureView(row: IssueListRow): IssueRowFigureView {
  return {
    parts: row.impactLabel.split(' vs '),
    tierState: TIER_STATE[row.tierTone],
    oneOff: row.impactUnitLabel === `${CURRENCY_GLYPH}/one-off`,
    note: issueFigureNote(row).replace(/ · /g, '\u00a0· '),
  };
}

/** Attribution words another mark on the row already says: the owner row lists
 *  its shares, the unit label says one-off, the tier pill says Watch. */
const ATTRIBUTION_SAID_ELSEWHERE = new Set(['Owner', 'One-off bearing stop comparison', 'Watch']);

/** The small line under a row's figure on the Senior metallurgist landing
 *  (polish.P27 comp A): attribution, contribution and the secondary figure,
 *  each once. */
export function issueFigureNote(row: IssueListRow): string {
  return [
    row.attributionLabel && !ATTRIBUTION_SAID_ELSEWHERE.has(row.attributionLabel) ? row.attributionLabel : undefined,
    row.contributorLabel,
    row.secondaryImpactLabel,
  ]
    .filter((part): part is string => Boolean(part))
    .join(' · ');
}
